import { Action, getModule, Module, Mutation, VuexModule } from "vuex-module-decorators"
import { apiUrl } from '@app/componentlib/src/application/helpers/api'
import { httpService } from '@app/componentlib/src/services/HttpService'
import store from '@app/componentlib/src/store'
import Vue from 'vue'
import { ICoordsFromAddressParams, ICountry, ILocationItem, IMapInformation, IRegion, ITypeAheadContainer} from '@app/componentlib/common/types/LocationTypes'
import { mapInformationList, countries } from '@app/componentlib/src/application/helpers/constants'
import { validCountries } from '@app/componentlib/src/application/helpers/constants'

export interface ILocationData {
  _initialized: boolean
  _locationTypeahead: ILocationItem[]
  _locations: IRegion[]
  _mapInformation: IMapInformation
  _countries: ICountry[]
  _selectedCountry: ICountry
}

const companySearchApi = 'CompanySearchApi'

@Module({
  dynamic: true,
  store,
  name: 'locationdata',
  namespaced: true
})

class LocationData extends VuexModule implements ILocationData {
  _initialized = false
  _locationTypeahead: ILocationItem[] = []
  _typeaheadContainer: ITypeAheadContainer = {}
  _locations: IRegion[] = []

  _mapInformations: IMapInformation[] = []
  _mapInformation: IMapInformation = {
    Country: '',
    ZipCode: '',
    Lat: 1,
    Lng: 1,
    Radius: 50,
    Zoom: 6,
    IsDraggable: true,
    Interactive: true
  }
  _countries: ICountry[] = []
  _selectedCountry: ICountry = this.countries[0]

  @Action({ rawError: true })
  async init (country: string) {
    this.context.commit('SET_INITIALIZED', true)
    console.log('LocationData: Initialized', country)
    await this.getFirstCountryTypeahead(country)
    await this.setLocationTypeahead(country)
  }

  @Action({ rawError: true })
  async getFirstCountryTypeahead(country: string) {
    // If country is not part of our valid array, set it to dk
    if (!validCountries.includes(country.toLowerCase()))
      country = 'dk'

    // Fetch the typeaheads for the country you want first
    const urlCountry = apiUrl(companySearchApi, 'GetFilters', {country: country.toUpperCase()})
    this._typeaheadContainer[country.toLowerCase()] = await this.getTypeaheadsFromBackend(urlCountry)
    return this._typeaheadContainer[country.toLowerCase()]
  }

  @Action({ rawError: true })
  async getOtherLocationTypeaheads (country: string) {
    // If country is not part of our valid array, set it to dk
    if (!validCountries.includes(country.toLowerCase()))
      country = 'dk'

    // Create all the urls for each country
    const urlDK = apiUrl(companySearchApi, 'GetFilters', {country: 'DK'})
    const urlNO = apiUrl(companySearchApi, 'GetFilters', {country: 'NO'})
    const urlSE = apiUrl(companySearchApi, 'GetFilters', {country: 'SE'})
    const urlFI = apiUrl(companySearchApi, 'GetFilters', {country: 'FI'})

    // Get all the typeahead except for the one that the country equeals to, because we fetched that in getFirstCountryTypeahead
    if (country.toLowerCase() !== 'dk')
      this._typeaheadContainer['dk'] = await this.getTypeaheadsFromBackend(urlDK)
    if (country.toLowerCase() !== 'se')
      this._typeaheadContainer['se'] = await this.getTypeaheadsFromBackend(urlSE)
    if (country.toLowerCase() !== 'no')
      this._typeaheadContainer['no'] = await this.getTypeaheadsFromBackend(urlNO)
    if (country.toLowerCase() !== 'fi')
      this._typeaheadContainer['fi'] = await this.getTypeaheadsFromBackend(urlFI)
  }

  @Action({ rawError: true })
  async getTypeaheadsFromBackend(url: string) {
    let tempArray: ILocationItem[] = []
    await httpService.get(url).then(data => {
      tempArray = data.typeahead.filter((obj: ILocationItem) => obj.type === "postal")
      tempArray.forEach((obj: ILocationItem) => obj.displayText = obj.postal + "$ " + obj.city)
    }).catch(error => console.log(error))

    return tempArray
  }

  @Action({ rawError: true })
  setLocationTypeahead (country: string) {
    this.SET_LOCATIONTYPEAHEAD(this._typeaheadContainer[country.toLowerCase()])
  }

  @Action({ rawError: true })
  setMapInformation(mapInformation: IMapInformation) {
    this.SET_MAPINFORMATION(mapInformation)
  }

  @Action({ rawError: true })
  setMapInformationRadius(radius: number) {
    let tempMap = this.mapInformation
    tempMap.Radius = radius
    this.SET_MAPINFORMATION(tempMap)
  }

  @Action({ rawError: true })
  setLocation (location: IRegion[]) {
    this.SET_LOCATION(location)
  }

  @Action({ rawError: true })
  setSelectedCountry (country: ICountry) {
    this.SET_SELECTEDCOUNTRY(country)
    this.setLocationTypeahead(country.countryCode)
  }

  @Action({ rawError: true })
  setSelectedCountryBasedOnString (country: string) {
    let tempCountry = LocationModule.countries.find(x => x.countryCode.toLowerCase() === country)
    if (tempCountry !== undefined)
      this.setSelectedCountry(tempCountry)
  }

  @Action({ rawError: true })
  async getCoordinatesFromAddress(params: ICoordsFromAddressParams) {
    const query = apiUrl('Maps', 'CoordinateFromAddress', {street: params.address,
    postalcode: params.postal, country: params.country})
    await httpService.get(query).then((data) => {
      if (data === null) return

      let mapInfo = this.mapInformation
      mapInfo.Lat = data.Latitude
      mapInfo.Lng = data.Longitude
      this.setMapInformation(mapInfo)
    })
  }

  @Action({ rawError: true })
  async GetAddressFromCoordinates(coords: any) {
    const query = apiUrl('Maps', 'AddressFromCoordinate', {latitude: coords.lat, longitude: coords.lon})
    let newRegion = undefined
    await httpService.get(query).then((data) => {
      if (data === null) return

      console.log("DATA", data)
      let locName = ""
      if (data.City !== null) {
        locName = data.City
      } else if (data.Municipality !== null) {
        locName = data.Municipality.replace("Municipality", "")
      } else {
        locName = ""
      }

      newRegion = {
        city: locName,
        lat: coords.lat,
        lon: coords.lon,
        postal: data.PostalCode,
        mapViewText: data.PostalCode + " " + locName + " - " + "Radius: " + this.mapInformation.Radius + " km",
        radius: this.mapInformation.Radius,
        type: 'city'
      } as IRegion

      let mapinfo = this.mapInformation
      mapinfo.Lat = coords.lat
      mapinfo.Lng = coords.lon
      this.setMapInformation(mapinfo)

      let tempCountry = LocationModule.countries.find(x => x.countryName === data.Country)

      if (tempCountry !== undefined)
        LocationModule.setSelectedCountry(tempCountry)
    })

    return newRegion
  }

  @Action({ rawError: true })
  initCountryAndMap (countriesToExclude: string[]) {
    if (countriesToExclude !== undefined) {
      const countriesToSet = countries.filter(country => !countriesToExclude.includes(country.countryCode))
      const mapInfoesToSet = mapInformationList.filter(mapInfo => !countriesToExclude.includes(mapInfo.Country))
      this.SET_COUNTRIES(countriesToSet)
      this.SET_MAPINFORMATIONS(mapInfoesToSet)
    } else {
      this.SET_COUNTRIES(countries)
      this.SET_MAPINFORMATIONS(mapInformationList)
    }
  }

  @Mutation
  SET_INITIALIZED (val: boolean) {
    this._initialized = val
  }

  @Mutation
  SET_LOCATIONTYPEAHEAD(locationTypeahead: ILocationItem[]) {
    Vue.set(this, "_locationTypeahead", locationTypeahead)
  }

  @Mutation
  SET_MAPINFORMATION(mapInformation: IMapInformation) {
    Vue.set(this, "_mapInformation", mapInformation)
  }

  @Mutation
  SET_LOCATION(location: IRegion[]) {
    Vue.set(this, "_locations", location)
  }

  @Mutation
  SET_SELECTEDCOUNTRY(country: ICountry) {
    Vue.set(this, "_selectedCountry", country)
  }

  @Mutation
  SET_TYPEAHEADCONTAINER(container: ITypeAheadContainer) {
    Vue.set(this, "_typeaheadContainer", container)
  }

  @Mutation
  SET_COUNTRIES(countries: ICountry[]) {
    Vue.set(this, "_countries", countries)
  }

  @Mutation
  SET_MAPINFORMATIONS(mapInfos: IMapInformation[]) {
    Vue.set(this, "_mapInformations", mapInfos)
  }

  // Getters
  get initialized (): boolean {
    return this._initialized
  }

  get locationTypeahead (): ILocationItem[] {
    return this._locationTypeahead
  }

  get mapInformation (): IMapInformation {
    return this._mapInformation
  }

  get locations (): IRegion[] {
    return this._locations
  }

  get countries (): ICountry[] {
    return this._countries
  }

  get selectedCountry (): ICountry {
    return this._selectedCountry
  }

  get mapInformations (): IMapInformation[] {
    return this._mapInformations
  }
}

export const LocationModule = getModule(LocationData)
